import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  Layers,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import {
  CASCADE_NODE_WARN_THRESHOLD,
  type Cascade,
  type CascadeNode,
  cascadeNodesPerLevel,
  countCascadeNodes,
  type FieldConfig,
  MAX_CASCADE_LEVELS,
  newCascadeId,
  updateCascadeNodes,
} from '../elements/fieldConfig';
import { CascadeImportDialog } from './CascadeImportDialog';

/**
 * Builds a dependent-dropdown chain — Country → State → District, with the tiers named by whoever
 * is building it.
 *
 * TWO THINGS SEPARATELY EDITED, because they are two different decisions:
 *
 *   • the LEVELS — how many dropdowns and what each one asks. One list, edited once.
 *   • the TREE — which options each choice leads to. Nested, expanded branch by branch.
 *
 * The shape this replaced conflated them: every dependent block carried its own `title`, so a
 * three-branch cascade held the same question three times and could word it three ways. It also
 * linked a block to its parent by matching the parent's TEXT, so correcting a typo detached
 * everything beneath it. Levels own the questions; nodes own stable ids.
 *
 * The tree is deliberately NOT rendered fully expanded. A country with thirty states has thirty
 * collapsed rows, and expanding one is how you look at one branch without the other twenty-nine on
 * screen.
 */
export function CascadeEditor({
  config,
  onChange,
}: {
  config: FieldConfig;
  onChange: (next: FieldConfig) => void;
}) {
  const cascade = config.cascade;

  // Split in two so the editor below receives a non-optional `Cascade`. Keeping it in one component
  // means every helper has to re-check a value the early return already proved — function
  // declarations are hoisted, so TypeScript cannot carry the narrowing into them.
  return cascade ? (
    <CascadeBody cascade={cascade} config={config} onChange={onChange} />
  ) : (
    <CascadePrompt config={config} onChange={onChange} />
  );
}

/**
 * Example level names, matching the sample sheet's hierarchy so the builder and the import teach the
 * same thing.
 */
const LEVEL_TITLE_HINTS = ['Ex. Country', 'Ex. State', 'Ex. District', 'Ex. Taluka', 'Ex. Village'];

/** Shown when the field is still a plain dropdown. */
function CascadePrompt({
  config,
  onChange,
}: {
  config: FieldConfig;
  onChange: (next: FieldConfig) => void;
}) {
  function enableCascade() {
    // Seeded from the field's existing plain options, so turning a working dropdown into a cascade
    // does not throw away what was already typed into it.
    const seeded = (config.options ?? []).map((label) => ({ id: newCascadeId('n'), label }));

    onChange({
      ...config,
      cascade: {
        // Titles start EMPTY, not "Level 1" / "Level 2". A level's title is now what the applicant
        // reads above that dropdown, so a seeded placeholder would ship the word "Level 2" to a
        // real person. Empty leaves the builder showing its own "Ex. Country" hints, which is what
        // invites filling them in.
        levels: [
          { id: newCascadeId('lv'), title: '', placeholder: 'Select an option' },
          { id: newCascadeId('lv'), title: '', placeholder: 'Select an option' },
        ],
        nodes: seeded.length > 0 ? seeded : [{ id: newCascadeId('n'), label: 'Option 1' }],
      },
    });
  }

  return (
    // NO IMPORT BUTTON HERE, deliberately. The options editor sits directly above this box and its
    // own Import already handles both shapes — a multi-column sheet turns the field into a cascade
    // by itself. A second button labelled the same thing, two inches away, doing something subtly
    // different is how somebody downloads a one-column sample expecting levels.
    <div className="mt-4 flex flex-col gap-3 rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <p className="text-[length:var(--text-12)] leading-relaxed text-[#64748B]">
        Ask follow-up questions based on the answer — Country, then State, then District. Or import
        a sheet above with one column per dropdown.
      </p>
      <button
        type="button"
        onClick={enableCascade}
        className="flex shrink-0 items-center gap-1.5 text-[length:var(--text-14)] font-semibold text-primary transition-colors hover:underline"
      >
        <Layers size={14} strokeWidth={2.5} />
        Add dependent dropdowns
      </button>
    </div>
  );
}

/** The real editor, once a cascade exists. */
function CascadeBody({
  cascade,
  config,
  onChange,
}: {
  cascade: Cascade;
  config: FieldConfig;
  onChange: (next: FieldConfig) => void;
}) {
  /** Expanded branches, keyed by the joined id path — the only stable identity a branch has. */
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [importing, setImporting] = useState(false);
  const keyOf = (path: readonly string[]) => path.join('/');
  const isExpanded = (path: readonly string[]) => expanded[keyOf(path)] === true;
  const toggle = (path: readonly string[]) =>
    setExpanded((prev) => ({ ...prev, [keyOf(path)]: !prev[keyOf(path)] }));

  function write(next: Cascade) {
    onChange({ ...config, cascade: next });
  }

  function updateLevel(index: number, patch: Partial<Cascade['levels'][number]>) {
    write({
      ...cascade,
      levels: cascade.levels.map((l, i) => (i === index ? { ...l, ...patch } : l)),
    });
  }

  function addLevel() {
    if (cascade.levels.length >= MAX_CASCADE_LEVELS) return;
    write({
      ...cascade,
      levels: [
        ...cascade.levels,
        // Empty title, for the same reason as `enableCascade` — it is applicant-facing text.
        { id: newCascadeId('lv'), title: '', placeholder: 'Select an option' },
      ],
    });
  }

  /**
   * Removing the LAST level also prunes the options that lived at that depth. Leaving them would
   * keep a tier of data no dropdown renders — invisible in the builder, dead weight in the stored
   * config, and quietly resurrected if the level were ever added back.
   *
   * Only the last level can go. Removing a middle tier would have to either orphan or re-parent
   * everything below it, and neither is a decision this editor should make on someone's behalf.
   */
  function removeLastLevel() {
    if (cascade.levels.length <= 1) return;
    const depth = cascade.levels.length - 1;
    write({ levels: cascade.levels.slice(0, depth), nodes: pruneBelow(cascade.nodes, depth) });
  }

  function disableCascade() {
    // The first tier's labels become plain options, so switching back leaves a working dropdown
    // rather than an empty one.
    const { cascade: _dropped, ...rest } = config;
    onChange({ ...rest, options: cascade.nodes.map((n) => n.label) });
  }

  return (
    <div className="mt-4 flex flex-col gap-4">
      {/* ── Levels ── */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="block text-[length:var(--text-14)] text-black">
            Dropdown levels
            {/* Stated explicitly, because the field's Title input sits directly above this block and
                it is otherwise a fair assumption that IT names the first dropdown. */}
            <span className="ml-1.5 font-normal text-[length:var(--text-12)] text-[#64748B]">
              — each name is the label the applicant sees above that dropdown
            </span>
          </span>
          <button
            type="button"
            onClick={disableCascade}
            className="flex items-center gap-1.5 text-[length:var(--text-12)] font-medium text-[#64748B] transition-colors hover:text-destructive"
          >
            <X size={13} />
            Remove dependency
          </button>
        </div>

        <div className="flex flex-col gap-2">
          {cascade.levels.map((level, index) => (
            <div key={level.id} className="flex items-center gap-2">
              <span className="w-14 shrink-0 text-[length:var(--text-12)] font-medium text-[#64748B]">
                Level {index + 1}
              </span>
              <input
                type="text"
                value={level.title}
                // A hint for EVERY level, not only the first three — a fourth and fifth level with a
                // blank box and no example read as broken inputs.
                placeholder={LEVEL_TITLE_HINTS[index] ?? `Ex. name for level ${index + 1}`}
                onChange={(e) => updateLevel(index, { title: e.target.value })}
                className="min-h-[38px] min-w-0 flex-1 rounded-lg border border-placeholder bg-white px-3 py-2 text-[length:var(--text-14)] text-[#323338] outline-none transition-all placeholder:text-[#94A3B8] focus:border-primary"
              />
              <input
                type="text"
                value={level.placeholder}
                placeholder="Placeholder"
                onChange={(e) => updateLevel(index, { placeholder: e.target.value })}
                className="min-h-[38px] hidden min-w-0 flex-1 rounded-lg border border-placeholder bg-white px-3 py-2 text-[length:var(--text-14)] text-[#323338] outline-none transition-all placeholder:text-[#94A3B8] focus:border-primary sm:block"
              />
              {index === cascade.levels.length - 1 && cascade.levels.length > 1 && (
                <button
                  type="button"
                  aria-label={`Remove level ${index + 1}`}
                  title="Remove the last level"
                  onClick={removeLastLevel}
                  className="shrink-0 p-1 text-destructive transition-colors hover:text-[#DC2626]"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          ))}
        </div>

        {cascade.levels.length < MAX_CASCADE_LEVELS ? (
          <button
            type="button"
            onClick={addLevel}
            className="flex items-center gap-1.5 self-start text-[length:var(--text-14)] font-semibold text-primary transition-colors hover:underline"
          >
            <Plus size={14} strokeWidth={2.5} />
            Add level
          </button>
        ) : (
          <span className="text-[length:var(--text-12)] text-[#94A3B8]">
            {MAX_CASCADE_LEVELS} levels is the maximum.
          </span>
        )}
      </div>

      {/* ── Tree ── */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="block text-[length:var(--text-14)] text-black">
            Options
            <span className="ml-1.5 font-normal text-[length:var(--text-12)] text-[#64748B]">
              — expand an option to set what comes next
            </span>
          </span>
          <button
            type="button"
            onClick={() => setImporting(true)}
            className="flex shrink-0 items-center gap-1.5 text-[length:var(--text-12)] font-semibold text-primary transition-colors hover:underline"
          >
            <FileSpreadsheet size={13} strokeWidth={2.5} />
            Import from sheet
          </button>
        </div>

        <CascadeSummary cascade={cascade} />

        <NodeList
          nodes={cascade.nodes}
          path={[]}
          depth={0}
          cascade={cascade}
          isExpanded={isExpanded}
          onToggle={toggle}
          onWrite={write}
        />
      </div>

      <CascadeImportDialog
        open={importing}
        // Cascade only: this field already HAS levels, so a one-column sheet would be a step
        // backwards rather than an import, and is refused with that explanation.
        allow="cascade"
        // Passed so Merge is available and so the sample sheet matches this field's level count.
        existing={cascade}
        onCancel={() => setImporting(false)}
        onApplyCascade={(next) => {
          write(next);
          setImporting(false);
        }}
      />
    </div>
  );
}

/**
 * How big the cascade currently is.
 *
 * WORTH THE LINE because the tree is collapsed by default: a cascade with 4,000 options looks
 * exactly like one with four until you expand it, so without a count there is nothing on screen to
 * tell an admin their form now carries a payload. Above the threshold it also says why that matters
 * — the whole tree ships to every applicant with the step.
 */
function CascadeSummary({ cascade }: { cascade: Cascade }) {
  const total = countCascadeNodes(cascade);
  if (total === 0) return null;

  const perLevel = cascadeNodesPerLevel(cascade);
  const heavy = total > CASCADE_NODE_WARN_THRESHOLD;

  return (
    <div className="flex flex-col gap-1.5">
      <p className="m-0 text-[length:var(--text-12)] text-[#64748B]">
        {total.toLocaleString()} {total === 1 ? 'option' : 'options'}
        {perLevel.length > 1 && (
          <span className="ml-1">
            (
            {perLevel
              .map(
                (count, index) =>
                  `${count.toLocaleString()} ${cascade.levels[index]?.title || `level ${index + 1}`}`,
              )
              .join(' · ')}
            )
          </span>
        )}
      </p>

      {heavy && (
        <p className="m-0 flex items-start gap-1.5 text-[length:var(--text-12)] leading-relaxed text-[#92400E]">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>
            This list is stored with the field, so every applicant downloads all of it when they
            reach this step. Consider asking for the deepest level as its own question.
          </span>
        </p>
      )}
    </div>
  );
}

/** Drops every node deeper than `depth` tiers. */
function pruneBelow(nodes: CascadeNode[], depth: number): CascadeNode[] {
  if (depth <= 1) {
    return nodes.map(({ id, label }) => ({ id, label }));
  }
  return nodes.map((node) => ({
    id: node.id,
    label: node.label,
    children: node.children ? pruneBelow(node.children, depth - 1) : undefined,
  }));
}

/**
 * One tier's options, each expandable into the tier below.
 *
 * Recursive, mirroring the data. `path` is the chain of ids above this list, which is both the key
 * for `updateCascadeNodes` and the expansion identity.
 */
function NodeList({
  nodes,
  path,
  depth,
  cascade,
  isExpanded,
  onToggle,
  onWrite,
}: {
  nodes: CascadeNode[];
  path: string[];
  depth: number;
  cascade: Cascade;
  isExpanded: (path: readonly string[]) => boolean;
  onToggle: (path: readonly string[]) => void;
  onWrite: (next: Cascade) => void;
}) {
  const level = cascade.levels[depth];
  /** True when a tier exists below this one, so these options can hold children. */
  const hasChildTier = depth + 1 < cascade.levels.length;

  function mutate(update: (siblings: CascadeNode[]) => CascadeNode[]) {
    onWrite({ ...cascade, nodes: updateCascadeNodes(cascade.nodes, path, update) });
  }

  return (
    <div className={cn('flex flex-col gap-2', depth > 0 && 'border-l border-[#E2E8F0] pl-3')}>
      {nodes.map((node) => {
        const nodePath = [...path, node.id];
        const open = isExpanded(nodePath);
        const childCount = node.children?.length ?? 0;

        return (
          <div key={node.id} className="flex flex-col gap-2">
            <div className="flex items-center gap-1.5">
              {hasChildTier ? (
                <button
                  type="button"
                  onClick={() => onToggle(nodePath)}
                  aria-expanded={open}
                  aria-label={`${open ? 'Collapse' : 'Expand'} ${node.label || 'option'}`}
                  className="shrink-0 rounded p-0.5 text-[#64748B] transition-colors hover:bg-gray-100 hover:text-[#0F172A]"
                >
                  {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                </button>
              ) : (
                // Alignment placeholder — without it a leaf tier's inputs sit 22px left of the
                // tier above and the tree looks misaligned rather than flat.
                <span className="w-[22px] shrink-0" aria-hidden />
              )}

              <input
                type="text"
                value={node.label}
                placeholder="Option"
                onChange={(e) =>
                  mutate((siblings) =>
                    siblings.map((s) => (s.id === node.id ? { ...s, label: e.target.value } : s)),
                  )
                }
                className="min-h-[38px] min-w-0 flex-1 rounded-lg border border-placeholder bg-white px-3 py-2 text-[length:var(--text-14)] text-[#0F172A] outline-none transition-all placeholder:text-[#94A3B8] focus:border-primary"
              />

              {hasChildTier && childCount > 0 && !open && (
                <span className="shrink-0 text-[length:var(--text-12)] text-[#64748B]">
                  {childCount} {level ? '' : ''}
                  {childCount === 1 ? 'option' : 'options'}
                </span>
              )}

              {nodes.length > 1 && (
                <button
                  type="button"
                  aria-label={`Delete ${node.label || 'option'}`}
                  // Deleting a branch takes everything under it, which is why the count above is
                  // shown on a collapsed row — so nobody deletes thirty districts believing they
                  // are deleting one state.
                  onClick={() => mutate((siblings) => siblings.filter((s) => s.id !== node.id))}
                  className="shrink-0 p-1 text-destructive transition-colors hover:text-[#DC2626]"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>

            {hasChildTier && open && (
              <div className="ml-[22px] flex flex-col gap-2">
                <span className="text-[length:var(--text-12)] font-medium text-[#64748B]">
                  {cascade.levels[depth + 1]?.title || `Level ${depth + 2}`} for “
                  {node.label || 'this option'}”
                </span>
                <NodeList
                  nodes={node.children ?? []}
                  path={nodePath}
                  depth={depth + 1}
                  cascade={cascade}
                  isExpanded={isExpanded}
                  onToggle={onToggle}
                  onWrite={onWrite}
                />
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={() =>
          mutate((siblings) => [
            ...siblings,
            { id: newCascadeId('n'), label: `Option ${siblings.length + 1}` },
          ])
        }
        className="flex items-center gap-1.5 self-start text-[length:var(--text-12)] font-semibold text-primary transition-colors hover:underline"
      >
        <Plus size={13} strokeWidth={2.5} />
        Add {level?.title?.trim() ? level.title.toLowerCase() : 'option'}
      </button>
    </div>
  );
}
